import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { SourceResponseModel } from './models/source-response-model';

@Injectable({
  providedIn: 'root',
})
export class SourcesAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const publishingSourceFilter = this.getStateFilterValue(filter, 'publishingSource');
        if (publishingSourceFilter != undefined && publishingSourceFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}publishingSource=${encodeURIComponent(publishingSourceFilter)}`);
        }

        const descriptionFilter = this.getStateFilterValue(filter, 'description');
        if (descriptionFilter != undefined && descriptionFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}description=${encodeURIComponent(descriptionFilter)}`);
        }

        const businessContactEmailFilter = this.getStateFilterValue(filter, 'businessContactEmail');
        if (businessContactEmailFilter != undefined && businessContactEmailFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}businessContactEmail=${encodeURIComponent(businessContactEmailFilter)}`);
        }

        const technicalContactEmailFilter = this.getStateFilterValue(filter, 'technicalContactEmail');
        if (technicalContactEmailFilter != undefined && technicalContactEmailFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}technicalContactEmail=${encodeURIComponent(technicalContactEmailFilter)}`);
        }

        const serviceNowGroupFilter = this.getStateFilterValue(filter, 'serviceNowGroup');
        if (serviceNowGroupFilter != undefined && serviceNowGroupFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}serviceNowGroup=${encodeURIComponent(serviceNowGroupFilter)}`);
        }

        const isActiveFilter = this.getStateFilterValue(filter, 'isActive');
        if (isActiveFilter != undefined) {
            queryString = queryString.concat(`${queryStart}isActive=${encodeURIComponent(isActiveFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `sources/search` + queryString)
          .pipe(
              map(
              (response: GridDataResult) => {
                  return {
                      data: response.data,
                      total: response.total
                  };
              }
              ), tap(() => (this.loading = false))
          );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }
}

@Injectable()
export class SourcesService extends SourcesAPIService {
    constructor(http: HttpClient) {
        super(http);
    }

    public create(model: SourceResponseModel): Observable<SourceResponseModel> {
        return this.http.post<SourceResponseModel>(this.baseUrlLocal + `sources`, model);
    }

    public update(publishingSource: string, model: SourceResponseModel): Observable<SourceResponseModel> {
        return this.http.put<SourceResponseModel>(this.baseUrlLocal + `sources/${btoa(publishingSource)}`, model);
    }

    public delete(publishingSource: string): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `sources/${btoa(publishingSource)}`);
    }
}
